import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, CalendarDays, Users, Check, Clapperboard, Clock } from "lucide-react";
import { api, formatApiError, formatDate } from "../../lib/api";
import { Switch } from "../ui/switch";
import { Link } from "react-router-dom";
import { CallShare } from "../CallShare";

const daysLeft = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const today = new Date();
  return Math.round((new Date(y, m - 1, d) - new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000);
};

export const Countdown = ({ c }) => {
  if (!c.respond_by_date || c.open === false) return null;
  const n = daysLeft(c.respond_by_date);
  if (n < 0) return null;
  const text = n === 0 ? "Last day to reply" : n === 1 ? "Closes tomorrow" : `Closes in ${n} days`;
  return <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${n <= 2 ? "bg-primary text-primary-foreground" : "border border-white/20 text-white/70"}`} data-testid={`call-countdown-${c.id}`}><Clock size={12} /> {text}</span>;
};

export const CallCard = ({ c, action, full }) => (
  <div className={`border p-6 ${c.responded ? "border-primary/60 bg-primary/5" : "border-white/10 bg-card"}`} data-testid={`casting-call-${c.id}`}>
    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
      <div>
        <div className="flex flex-wrap items-center gap-3"><p className="label-xs text-primary">{c.production_type || "Production"}</p><Countdown c={c} /></div>
        <h3 className="font-display text-2xl mt-2"><Link to={`/casting-calls/${c.id}`} className="hover:text-primary transition-colors" data-testid={`casting-call-link-${c.id}`}>{c.production_title || `${c.extras_needed} extras needed`}</Link></h3>
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm text-white/70">
          <span className="flex items-center gap-2"><MapPin size={14} className="text-primary" />{c.location}, {c.country}{c.distance_miles != null && ` · ${c.distance_miles} mi from you`}</span>
          <span className="flex items-center gap-2"><CalendarDays size={14} className="text-primary" />{c.shoot_dates}</span>
          <span className="flex items-center gap-2"><Users size={14} className="text-primary" />{c.extras_needed} needed</span>
        </div>
      </div>
      {action}
    </div>
    <p className="text-white/70 mt-4 whitespace-pre-line">{c.requirements}</p>
    <div className="flex flex-wrap gap-x-6 mt-4 text-xs text-white/50">
      {c.pay_rate && <span>Pay: {c.pay_rate}</span>}
      {(c.respond_by || c.respond_by_date) && <span data-testid={`call-reply-by-${c.id}`}>Reply by: {[c.respond_by_date && formatDate(c.respond_by_date), c.respond_by].filter(Boolean).join(" · ")}</span>}
    </div>
    <div className="mt-5 pt-4 border-t border-white/10"><CallShare c={c} full={full} /></div>
  </div>
);

const RespondButton = ({ c, onToggle, canRespond }) => (
  <button onClick={() => onToggle(c)} disabled={!canRespond} className={`shrink-0 px-5 py-3 font-bold uppercase text-xs tracking-wider transition-colors disabled:opacity-40 ${c.responded ? "border border-primary text-primary" : "bg-primary text-primary-foreground hover:bg-[hsl(43,100%,60%)]"}`} data-testid={`casting-respond-${c.id}`}>
    {c.responded ? <span className="inline-flex items-center gap-2"><Check size={14} /> You're available</span> : "I'm available"}
  </button>
);

const EmailToggle = ({ profile }) => {
  const [on, setOn] = useState(profile.casting_emails !== false);
  const change = async (v) => {
    setOn(v);
    try {
      await api.patch("/profile/me/casting-emails", { enabled: v });
      toast.success(v ? "You'll get emails about casting calls near you" : "Casting call emails turned off");
    } catch (err) {
      setOn(!v);
      toast.error(formatApiError(err));
    }
  };
  return (
    <label className="flex items-center justify-between gap-4 border border-white/10 p-4 max-w-2xl cursor-pointer" data-testid="casting-email-toggle-row">
      <span className="text-sm"><span className="font-bold block">Email me about casting calls near me</span><span className="text-white/50">Uses the postcode on your profile.</span></span>
      <Switch checked={on} onCheckedChange={change} data-testid="casting-email-toggle" />
    </label>
  );
};

export const CastingCalls = ({ profile }) => {
  const [calls, setCalls] = useState(null);
  useEffect(() => {
    api.get("/casting-calls").then((r) => setCalls(r.data)).catch(() => setCalls([]));
  }, []);

  const toggle = async (c) => {
    try {
      const { data } = await api.post(`/casting-calls/${c.id}/respond`);
      setCalls((list) => list.map((x) => (x.id === c.id ? { ...x, responded: data.responded } : x)));
      toast.success(data.responded ? "Thanks — we'll pass your profile to the production" : "Removed your availability");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (calls === null) return <p className="label-xs">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="casting-calls-panel">
      <p className="text-white/60 text-sm max-w-2xl">Productions send us requests for groups of extras. Tap "I'm available" and we'll send your profile to the production team. Your contact details aren't shared unless you've chosen to show them on your profile.</p>
      <EmailToggle profile={profile} />
      {!profile.active && <p className="text-sm text-primary" data-testid="casting-not-live">Your profile needs to be live before you can put yourself forward.</p>}
      {!calls.length && (
        <div className="border border-dashed border-white/15 p-16 text-center" data-testid="casting-calls-empty">
          <Clapperboard className="mx-auto text-white/20" size={48} />
          <p className="font-display text-2xl mt-6">No casting calls right now</p>
          <p className="text-white/50 text-sm mt-2">New requests from productions will appear here, nearest to you first.</p>
        </div>
      )}
      {calls.map((c) => <CallCard key={c.id} c={c} action={<RespondButton c={c} onToggle={toggle} canRespond={profile.active} />} />)}
    </div>
  );
};
